import React, { useMemo } from 'react';
import { Link as GatsbyLink, useStaticQuery, graphql } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Link, Brand } from '@printdeal/ui-library';
import { getConfiguration } from '../../../helpers/Configuration';
import ConversationLoopLink from '../../customer-programs/conversation-loop/ConversationLoopLink';
import { findNodeByLocale } from '../../../helpers/translations';
import NavigationExperiment from '../NavigationExperiment';
import { type NavigationContainer } from '../types';

type QueryResult = {
  allContentfulNavigationContainer: {
    nodes: NavigationContainer[]
  } | undefined
};

const FinePrintNavigation = ({ navigation }: { navigation: NavigationContainer['navigation'] }) => (
  <nav className="pd-grid pd-grid-cols-2 pd-gap-3 lg:pd-flex lg:pd-text-sm" aria-label={navigation.title}>
    {'items' in navigation && navigation.items?.map((item) => (
      <Link
        key={item.id}
        as={GatsbyLink}
        to={item.linkedEntry?.path || item.path}
        iconBefore="chevron-right"
        variant="secondary"
      >
        {item.title || item.linkedEntry?.title}
      </Link>
    ))}
    <ConversationLoopLink linkIcon="chevron-right" />
  </nav>
);

export const Footer = () => {
  const intl = useIntl();
  const configuration = getConfiguration();

  const { allContentfulNavigationContainer } = useStaticQuery<QueryResult>(graphql`
    query FinePrintFooter {
      allContentfulNavigationContainer(
        filter: { type: { eq: "finePrint" }}
      ) {
        nodes {
          ...NavigationContainer
        }
      }
    }
  `);

  const navigationForLocale: NavigationContainer['navigation'] = useMemo(() => findNodeByLocale(
    allContentfulNavigationContainer?.nodes || [],
    intl.locale,
  )?.navigation || ({} as NavigationContainer['navigation']), [allContentfulNavigationContainer, intl.locale]);

  return (
    <div className="pd-bg-dim-100 pd-py-6 pd-text-sm">
      <Container className="pd-flex pd-flex-col pd-justify-between pd-gap-6 sm:pd-flex-row sm:pd-align-baseline">
        {configuration.channel.brand === 'printdeal' && (
          <figure>
            <Brand
              brand="printdeal"
              aria-label="Printdeal logo"
              className="pd-h-8"
            />
          </figure>
        )}
        <NavigationExperiment
          as={FinePrintNavigation}
          navigation={navigationForLocale}
        />
        <span className="pd-text-sm pd-leading-loose pd-text-dim-800">
          &copy;
          {` ${new Date().getFullYear()} `}
          <FormattedMessage id="footer.brandName" defaultMessage="Printdeal B.V." />
        </span>
      </Container>
    </div>
  );
};
